import { useMemo } from 'react';

import { PatientStatusCard, TagColor } from '@equip.health/ui';

import OverviewLoading from '~/components/common/OverviewLoading';
import { PATIENT_HEADER_TOP_LEVEL_ID } from '~/components/maud/patients/profile/patient-header/patientHeaderConstants';
import { NoteStatus } from '~/lib/constants/AddPatientNoteModal.constants';
import { InquiryStatusValue } from '~/lib/constants/inquiry.constants';
import {
  PatientUniversalStatusCode,
  TreatmentEpisodeStatus,
} from '~/lib/constants/patients.constants';
import {
  determinePatientInquiryStatus,
  treatmentEpisodeColorMapper,
} from '~/lib/util/patient.util';
import { useGetPatientTreatmentEpisodesQuery } from '~/store/services/clinicalData';
import {
  useGetInquiryDetailByIdQuery,
  useGetPatientInquiriesSummaryQuery,
} from '~/store/services/inquiry';
import { useGetPatientNotesQuery } from '~/store/services/notes/notes';
import { PatientStatusHeaderPopover } from '~/components/maud/patients/profile/patient-header/PatientStatusHeaderPopover';
import {
  formatInquiryText,
  formatTreatmentEpisodeText,
  inquiryStatusStylesMapper,
} from '~/lib/util/patientStatusHeader.util';

interface PatientStatusHeaderLegacyProps {
  patientId: string;
  patientStatus: string;
  patientStatusCode: string;
  patientStatusColor: TagColor;
  primaryDiagnosis?: string;
}

const defaultStatusLine = 'Unknown status';

export const PatientStatusHeaderLegacy = ({
  patientId,
  patientStatus,
  patientStatusCode,
  patientStatusColor,
  primaryDiagnosis,
}: PatientStatusHeaderLegacyProps) => {
  const {
    data: patientTreatmentEpisodes,
    isLoading: isTreatmentEpisodesLoading,
  } = useGetPatientTreatmentEpisodesQuery(patientId);
  const { data: patientInquirySummaries, isFetching: isFetchingSummary } =
    useGetPatientInquiriesSummaryQuery(patientId);

  const activeInquiry: GetPatientInquiriesSummaryResponse = useMemo(() => {
    return determinePatientInquiryStatus(
      patientInquirySummaries,
      patientStatus,
    );
  }, [patientInquirySummaries, patientStatus]);

  const shouldFetchInquiry: boolean = !!activeInquiry;

  const { data: activeInquiryDetail, isLoading: isActiveInquiryDetailLoading } =
    useGetInquiryDetailByIdQuery(activeInquiry?.id, {
      refetchOnMountOrArgChange: true,
      skip: !shouldFetchInquiry,
    });

  const shouldFetchNotes: boolean = useMemo(() => {
    return activeInquiryDetail?.id != null;
  }, [activeInquiryDetail?.id]);

  const { data: notes } = useGetPatientNotesQuery(
    { inquiryId: activeInquiryDetail?.id },
    {
      refetchOnMountOrArgChange: true,
      skip: !shouldFetchNotes,
    },
  );

  const lastContactDateISO: string | null | undefined = useMemo(() => {
    if (!notes || !Array.isArray(notes)) {
      return null;
    }

    return (notes ?? [])
      .filter(({ noteStatus }) => noteStatus !== NoteStatus.DRAFT)
      .sort(
        (a, b) =>
          new Date(b.createdDateTime).getTime() -
          new Date(a.createdDateTime).getTime(),
      )[0]?.createdDateTime;
  }, [notes]);

  const isInquiryStatus = useMemo(
    () =>
      Object.values(InquiryStatusValue).includes(
        patientStatus as InquiryStatusValue,
      ),
    [patientStatus],
  );
  const isTreatmentEpisodeStatus = useMemo(
    () =>
      Object.values(TreatmentEpisodeStatus).includes(
        patientStatus as TreatmentEpisodeStatus,
      ),
    [patientStatus],
  );

  let treatmentEpisode: TreatmentEpisode | undefined;
  if (isTreatmentEpisodeStatus) {
    const statusCodeToMatch =
      patientStatusCode === PatientUniversalStatusCode.PAUSED
        ? PatientUniversalStatusCode.IN_TREATMENT
        : patientStatusCode;
    treatmentEpisode = patientTreatmentEpisodes?.data?.find(
      (ep) => ep.status.code === statusCodeToMatch,
    );
  }

  const displayData: PatientStatusCardDisplayData = useMemo(() => {
    const display: PatientStatusCardDisplayData = {
      lineOne: defaultStatusLine,
      lineTwo: defaultStatusLine,
      statusColor: patientStatusColor,
    };

    if (
      isInquiryStatus &&
      activeInquiryDetail?.status.name === InquiryStatusValue[patientStatusCode]
    ) {
      const inquiryDisplay = formatInquiryText(
        InquiryStatusValue[patientStatusCode] as InquiryStatusValue,
        activeInquiryDetail,
        lastContactDateISO,
      );

      display.lineOne = inquiryDisplay.lineOne;
      display.lineTwo = inquiryDisplay.lineTwo;

      const inquiryStatusInfo =
        inquiryStatusStylesMapper[InquiryStatusValue[patientStatusCode]];
      if (inquiryStatusInfo) {
        display.statusColor = inquiryStatusInfo.tagColor;
      }
    }

    if (isTreatmentEpisodeStatus && patientTreatmentEpisodes?.data.length > 0) {
      if (treatmentEpisode) {
        const treatmentDisplay = formatTreatmentEpisodeText(
          patientStatusCode,
          treatmentEpisode,
          primaryDiagnosis,
        );

        display.lineOne = treatmentDisplay.lineOne;
        display.lineTwo = treatmentDisplay.lineTwo;
        if (patientStatusCode !== PatientUniversalStatusCode.PAUSED) {
          display.statusColor =
            treatmentEpisodeColorMapper[treatmentEpisode.status.description] ??
            display.statusColor;
        }
      }
    }

    return display;
  }, [
    activeInquiryDetail,
    isInquiryStatus,
    isTreatmentEpisodeStatus,
    lastContactDateISO,
    patientStatus,
    patientStatusCode,
    patientStatusColor,
    patientTreatmentEpisodes,
    primaryDiagnosis,
  ]);

  if (
    isTreatmentEpisodesLoading ||
    isFetchingSummary ||
    isActiveInquiryDetailLoading
  ) {
    return (
      <OverviewLoading
        height={48}
        id={`${PATIENT_HEADER_TOP_LEVEL_ID}__loading`}
      />
    );
  }

  return (
    <PatientStatusHeaderPopover
      inquiryDetail={
        activeInquiryDetail?.status.name === patientStatus
          ? activeInquiryDetail
          : undefined
      }
      lastContactDateISO={lastContactDateISO}
      patientId={patientId}
      patientStatus={patientStatus}
      pausedDetails={
        patientStatus === TreatmentEpisodeStatus.PAUSED
          ? treatmentEpisode?.pausedDetails
          : undefined
      }
      primaryDiagnosis={primaryDiagnosis}
      treatmentEpisode={treatmentEpisode}
      triggerContent={
        <PatientStatusCard
          id={PATIENT_HEADER_TOP_LEVEL_ID}
          lineOne={displayData.lineOne}
          lineTwo={displayData.lineTwo}
          statusColor={displayData.statusColor}
          statusLabel={patientStatus}
        />
      }
    />
  );
};
